import { useEffect, useMemo, useState } from 'react'
import { get } from 'aws-amplify/api'
import { useAuth } from '@/lib/auth/AuthContext'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'

const REFRESH_INTERVAL_MS = 60_000
type Environment = 'prod' | 'uat' | 'dev'
type PlatformKey = 'ios' | 'android' | 'unknown'

interface MetricPoint {
  timestamp: string
  value: number
}

interface VersionBreakdown {
  value: string
  platform: PlatformKey
  count: number
}

interface AudienceSummary {
  totalActiveAudience: number
  uniqueInstallations: number
  platformBreakdown: {
    ios: number
    android: number
    unknown: number
  }
  deliveryModeBreakdown: {
    iosBroadcast: number
    iosLiveActivityToken: number
    androidFcm: number
    unknown: number
  }
  activeIosRegistrations: number
  appVersions: VersionBreakdown[]
  osVersions: VersionBreakdown[]
}

interface EventMetric {
  eventId: string
  homeTeam: string
  awayTeam: string
  league: string
  isComplete: boolean
  eventStartTime: string
  latestSubscribers: number | null
  latestHourlySubscribers: number | null
  maxSubscribers: number | null
  minSubscribers: number | null
  points: MetricPoint[]
  current: AudienceSummary
}

interface MetricSeriesByPlatform {
  total: MetricPoint[]
  ios: MetricPoint[]
  android: MetricPoint[]
  unknown: MetricPoint[]
}

interface MatchMetricsResponse {
  generatedAt: string
  environment: string
  hours: number
  lookbackDays: number
  includesOlderMatches: boolean
  firstDeviceRegistrations: MetricSeriesByPlatform
  events: EventMetric[]
}

const sparkline = (points: MetricPoint[]) => {
  if (!points.length) return 'No data'

  const values = points.map((point) => point.value)
  const min = Math.min(...values)
  const max = Math.max(...values)

  if (max === min) {
    return '▁'.repeat(Math.min(values.length, 24))
  }

  const buckets = '▁▂▃▄▅▆▇█'
  return values
    .slice(-24)
    .map((value) => {
      const index = Math.round(((value - min) / (max - min)) * (buckets.length - 1))
      return buckets[index]
    })
    .join('')
}

const formatVersionBreakdowns = (versions: VersionBreakdown[]) => {
  if (!versions.length) return '—'

  return versions
    .slice(0, 3)
    .map((version) => `${version.platform} ${version.value} (${version.count})`)
    .join(', ')
}

const latestPointValue = (points: MetricPoint[]) => {
  if (!points.length) return 0
  return points[points.length - 1]?.value ?? 0
}

const formatEventStartTime = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

const getEventStatus = (eventStartTime: string, isComplete: boolean) => {
  if (isComplete) {
    return { label: 'Complete', className: 'bg-emerald-500/20 text-emerald-200' }
  }

  if (new Date(eventStartTime).getTime() > Date.now()) {
    return { label: 'Upcoming', className: 'bg-sky-500/20 text-sky-200' }
  }

  return { label: 'Active / Recent', className: 'bg-amber-500/20 text-amber-200' }
}

export default function MatchMetricsPage() {
  const { isAuthenticated, session } = useAuth()
  const { toast } = useToast()

  const [metrics, setMetrics] = useState<MatchMetricsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showOlderMatches, setShowOlderMatches] = useState(false)
  const [selectedEnvironment, setSelectedEnvironment] = useState<Environment>('prod')

  const fetchMetrics = async () => {
    if (!session?.tokens?.idToken) {
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const response = get({
        apiName: 'dashboard',
        path: '/data/matchMetrics',
        options: {
          queryParams: {
            environment: selectedEnvironment,
            includeOlderMatches: showOlderMatches ? 'true' : 'false',
          },
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.tokens.idToken}`,
          },
          withCredentials: true,
        },
      })

      const { body } = await response.response
      const raw = await body.json()
      setMetrics(raw as unknown as MatchMetricsResponse)
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Failed to load match metrics'
      setError(message)
      toast({
        title: 'Match metrics unavailable',
        description: message,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isAuthenticated) {
      setMetrics(null)
      return
    }

    fetchMetrics()
    const timer = window.setInterval(fetchMetrics, REFRESH_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [isAuthenticated, session?.tokens?.idToken, selectedEnvironment, showOlderMatches])

  const visibleEvents = useMemo(() => {
    if (!metrics?.events) return []
    return [...metrics.events].sort((left, right) => {
      const startDifference = new Date(right.eventStartTime).getTime() - new Date(left.eventStartTime).getTime()
      if (startDifference !== 0) {
        return startDifference
      }

      return left.eventId.localeCompare(right.eventId)
    })
  }, [metrics])

  const currentAudienceTotal = useMemo(
    () => visibleEvents.reduce((total, event) => total + event.current.totalActiveAudience, 0),
    [visibleEvents],
  )

  const currentIosBroadcastTotal = useMemo(
    () => visibleEvents.reduce((total, event) => total + event.current.deliveryModeBreakdown.iosBroadcast, 0),
    [visibleEvents],
  )

  const firstDeviceLastHour = useMemo(
    () => latestPointValue(metrics?.firstDeviceRegistrations.total ?? []),
    [metrics],
  )

  const firstDeviceIosLastHour = useMemo(
    () => latestPointValue(metrics?.firstDeviceRegistrations.ios ?? []),
    [metrics],
  )

  const firstDeviceAndroidLastHour = useMemo(
    () => latestPointValue(metrics?.firstDeviceRegistrations.android ?? []),
    [metrics],
  )

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Match Metrics</h1>
          <p className="text-sm text-slate-400">
            Historical totals are retained from CloudWatch. Current audience numbers come from NotificationCore state, including upcoming matches that already have deployed audience data.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={selectedEnvironment}
            onValueChange={(value) => setSelectedEnvironment(value as Environment)}
          >
            <SelectTrigger
              id="match-metrics-environment"
              aria-label="Match metrics environment"
              className="w-[110px] text-white"
            >
              <SelectValue placeholder="Environment" />
            </SelectTrigger>
            <SelectContent className="text-white">
              <SelectItem value="prod" className="text-white">prod</SelectItem>
              <SelectItem value="uat" className="text-white">uat</SelectItem>
              <SelectItem value="dev" className="text-white">dev</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="text-white" onClick={fetchMetrics} disabled={isLoading}>
            {isLoading ? 'Refreshing…' : 'Refresh now'}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Environment</CardDescription>
            <CardTitle>{metrics?.environment ?? '—'}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-slate-400">
            Generated: {metrics?.generatedAt ? new Date(metrics.generatedAt).toLocaleString() : '—'}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Tracked Events</CardDescription>
            <CardTitle>{visibleEvents.length}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-slate-400">
            Window: last {metrics?.lookbackDays ?? 14} days plus upcoming deployed matches
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Current Active Audience</CardDescription>
            <CardTitle>{currentAudienceTotal}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-slate-400">
            Broadcast rows: {currentIosBroadcastTotal}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>New First Device Registrations (Last Hour)</CardDescription>
            <CardTitle>{firstDeviceLastHour}</CardTitle>
          </CardHeader>
          <CardContent className="font-mono text-xs text-slate-400">
            iOS {firstDeviceIosLastHour} | Android {firstDeviceAndroidLastHour}
            <br />
            {sparkline(metrics?.firstDeviceRegistrations.total ?? [])}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Event Audience Trends</CardTitle>
            <CardDescription>
              The trend sparkline remains historical total audience. Current platform and delivery-path counts are shown alongside it.
            </CardDescription>
          </div>
          <Button variant="secondary" onClick={() => setShowOlderMatches((value) => !value)}>
            {showOlderMatches ? 'Hide Older Matches' : 'Show Older Matches'}
          </Button>
        </CardHeader>
        <CardContent>
          {error ? <p className="text-sm text-red-400">{error}</p> : null}

          {visibleEvents.length === 0 ? (
            <p className="text-sm text-slate-400">No events found for the reporting window.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event</TableHead>
                    <TableHead>Start</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Current</TableHead>
                    <TableHead>Platforms</TableHead>
                    <TableHead>History</TableHead>
                    <TableHead>Versions</TableHead>
                    <TableHead>Trend</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleEvents.map((event) => (
                    <TableRow key={event.eventId}>
                      <TableCell>
                        <div className="font-medium">
                          {event.homeTeam} vs {event.awayTeam}
                        </div>
                        <div className="font-mono text-xs text-slate-400">{event.eventId}</div>
                      </TableCell>
                      <TableCell>{formatEventStartTime(event.eventStartTime)}</TableCell>
                      <TableCell>
                        <Badge className={getEventStatus(event.eventStartTime, event.isComplete).className}>
                          {getEventStatus(event.eventStartTime, event.isComplete).label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{event.current.totalActiveAudience}</div>
                        <div className="text-xs text-slate-400">
                          installs {event.current.uniqueInstallations} | regs {event.current.activeIosRegistrations}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>iOS {event.current.platformBreakdown.ios}</div>
                        <div>Android {event.current.platformBreakdown.android}</div>
                        <div className="text-xs text-slate-400">
                          Broadcast {event.current.deliveryModeBreakdown.iosBroadcast} | Token {event.current.deliveryModeBreakdown.iosLiveActivityToken}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>Current {event.latestSubscribers ?? '—'}</div>
                        <div className="text-xs text-slate-400">
                          Hourly {event.latestHourlySubscribers ?? '—'} | High {event.maxSubscribers ?? '—'} | Low {event.minSubscribers ?? '—'}
                        </div>
                        {event.points.length === 0 ? (
                          <div className="text-[11px] text-slate-500">No CloudWatch history in the selected window yet.</div>
                        ) : null}
                      </TableCell>
                      <TableCell className="max-w-[260px]">
                        <div className="text-xs text-slate-300">{formatVersionBreakdowns(event.current.appVersions)}</div>
                        <div className="text-[11px] text-slate-500">{formatVersionBreakdowns(event.current.osVersions)}</div>
                      </TableCell>
                      <TableCell className="font-mono text-xs tracking-wide">{sparkline(event.points)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  )
}
